import { useState, useEffect, useCallback, useRef } from 'react';
import { list, count } from '../services/campaignService';

/**
 * 캠페인 목록 및 통계 조회 훅
 * @param {Object} config - 캠페인 설정 객체
 * @param {Object} filter - 필터 상태
 * @returns {Object} { items, summary, loading, error, refresh }
 */
export function useCampaignList(config, filter) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // filter 객체의 깊은 비교를 위한 ref
  const filterRef = useRef();
  const filterString = JSON.stringify(filter);

  /**
   * 데이터 조회
   */
  const fetchData = useCallback(async () => {
    if (!config) {
      console.warn('useCampaignList: config가 제공되지 않았습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 병렬로 목록과 통계 조회
      const [listResult, countResult] = await Promise.all([
        list(config, filter),
        count(config, filter)
      ]);

      setItems(listResult.items || []);
      
      // count API 응답 형식 변환 (total, waiting, processing, completed 등)
      const summaryData = countResult.summary || {};
      setSummary({
        total: summaryData.total || summaryData.totalCount || 0,
        waiting: summaryData.waiting || summaryData.waitingCount || 0,
        processing: summaryData.processing || summaryData.processingCount || 0,
        completed: summaryData.completed || summaryData.completedCount || 0,
        refund: summaryData.refund || summaryData.refundCount || 0,
        // 원본 데이터도 보존
        ...summaryData
      });
    } catch (err) {
      console.error('useCampaignList 오류:', err);
      setError(err);
      setItems([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [config, filter]);

  /**
   * 데이터 새로고침
   */
  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // filter 변경 시 자동 조회 (깊은 비교)
  useEffect(() => {
    // filter 객체가 실제로 변경되었는지 확인
    if (filterRef.current !== filterString) {
      filterRef.current = filterString;
      fetchData();
    }
  }, [filterString, fetchData]);

  return {
    items,
    summary,
    loading,
    error,
    refresh
  };
}

